/* ============================================
   Artemis Theme — Variant Hydration
   ============================================
   Shopify's storefront rendering layer caps product variant data at 250:
   Liquid's `product.variants`, `{{ product | json }}`, AND the /products/x.js
   & .json AJAX endpoints all truncate to the first 250 variants. A product can
   HOLD far more (up to ~2,000), so apparel with 50+ colors × sizes loses every
   variant past the cap. The option <select> still lists all values (option
   values aren't capped), but selecting one past the cap finds no variant — so
   image / price / stock never update on the PDP and the designer can't size it.

   The only storefront-accessible source that isn't capped is the Shopify
   Storefront GraphQL API (paginated, first-party). This module pulls the
   COMPLETE variant set for a product handle and exposes it normalized into the
   two shapes the rest of the theme already consumes:
     - toProductData(): the snake_case shape product-form.js expects.
     - toSnapshot():    the camelCase shape apparel-stock.js / designer.js use.

   Inert until a Storefront token is configured (Theme settings → Apparel
   Designer → Large-catalog support); callers keep the capped-but-working first
   250 when disabled or on any error.
   ============================================ */
(function () {
  'use strict';

  var cfg = window.SHOPIFY_STOREFRONT || {};
  var cache = {}; // handle -> Promise<normalized variants[] | null>, deduped per page

  function enabled() {
    return !!(cfg && cfg.token);
  }

  function endpoint() {
    // Served same-origin on the storefront domain — no CORS, no shop domain needed.
    return '/api/' + (cfg.version || '2025-01') + '/graphql.json';
  }

  // 250 is the Storefront API's max page size for a variant connection; we
  // paginate via the cursor to gather them all.
  var QUERY = [
    'query($handle:String!,$cursor:String){',
    '  product(handle:$handle){',
    '    options{name}',
    '    variants(first:250,after:$cursor){',
    '      pageInfo{hasNextPage endCursor}',
    '      nodes{',
    '        id title availableForSale quantityAvailable',
    '        price{amount} compareAtPrice{amount}',
    '        selectedOptions{name value} image{url}',
    '      }',
    '    }',
    '  }',
    '}'
  ].join('\n');

  function toCents(amount) {
    if (amount == null) return null;
    var n = parseFloat(amount);
    return isFinite(n) ? Math.round(n * 100) : null;
  }

  // Storefront ids are GIDs ("gid://shopify/ProductVariant/123"); the theme uses
  // the bare numeric id everywhere (cart add, variant matching), so peel it off.
  function numericId(gid) {
    var s = String(gid == null ? '' : gid);
    var i = s.lastIndexOf('/');
    return i >= 0 ? s.slice(i + 1) : s;
  }

  // One normalized variant carrying every field the theme's two shapes need.
  // selectedOptions arrive name-keyed; re-order them to the product's option
  // order so options[colorIdx]/options[sizeIdx] line up with the <select>s.
  function normalize(node, optionNames) {
    var byName = {};
    (node.selectedOptions || []).forEach(function (o) { byName[o.name] = o.value; });
    var options = optionNames.map(function (n) { return byName[n]; });
    return {
      id: numericId(node.id),
      title: node.title,
      available: !!node.availableForSale,
      priceCents: toCents(node.price && node.price.amount),
      compareAtCents: toCents(node.compareAtPrice && node.compareAtPrice.amount),
      // null when the merchant hasn't published inventory to the Storefront API;
      // apparel-stock.js degrades that to in/out via `available` (no low-stock).
      inventoryQuantity: (node.quantityAvailable == null ? null : node.quantityAvailable),
      inventoryManaged: null, // not exposed by Storefront; apparel-stock infers tracked-ness instead
      inventoryPolicy: null,  // availableForSale already reflects the oversell policy
      options: options,
      imageUrl: (node.image && node.image.url) || null,
    };
  }

  function fetchPage(handle, cursor) {
    return fetch(endpoint(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Shopify-Storefront-Access-Token': cfg.token,
      },
      body: JSON.stringify({ query: QUERY, variables: { handle: handle, cursor: cursor || null } }),
    }).then(function (r) {
      if (!r.ok) throw new Error('storefront ' + r.status);
      return r.json();
    });
  }

  // Resolve to the full normalized variant list for a handle (all pages), or
  // null if disabled / the product isn't found / the API errors. Cached per
  // handle so the PDP hook and the designer don't double-fetch.
  function fetchAll(handle) {
    if (!enabled() || !handle) return Promise.resolve(null);
    if (cache[handle]) return cache[handle];

    var all = [];
    var optionNames = null;

    function loop(cursor) {
      return fetchPage(handle, cursor).then(function (json) {
        var product = json && json.data && json.data.product;
        if (!product) return null;
        if (!optionNames) optionNames = (product.options || []).map(function (o) { return o.name; });
        var conn = product.variants || {};
        (conn.nodes || []).forEach(function (n) { all.push(normalize(n, optionNames)); });
        if (conn.pageInfo && conn.pageInfo.hasNextPage) return loop(conn.pageInfo.endCursor);
        return all;
      });
    }

    var p = loop(null).catch(function () { return null; });
    cache[handle] = p;
    return p;
  }

  window.ArtemisVariants = {
    enabled: enabled,
    fetchAll: fetchAll,

    // product-form.js shape: { id, options, available, price(cents),
    // compare_at_price(cents), featured_image:{src} }.
    toProductData: function (list) {
      return (list || []).map(function (v) {
        return {
          id: v.id,
          options: v.options,
          available: v.available,
          price: v.priceCents,
          compare_at_price: v.compareAtCents,
          featured_image: v.imageUrl ? { src: v.imageUrl } : null,
        };
      });
    },

    // apparel-stock.js / designer.js snapshot shape.
    toSnapshot: function (list) {
      return (list || []).map(function (v) {
        return {
          id: v.id,
          title: v.title,
          priceCents: v.priceCents,
          available: v.available,
          inventoryQuantity: v.inventoryQuantity,
          inventoryManaged: v.inventoryManaged,
          inventoryPolicy: v.inventoryPolicy,
          options: v.options,
          featuredImage: v.imageUrl,
        };
      });
    },
  };
})();
